import { X, Eye, Image as ImageIcon, Lock, Globe } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import Modal from '../core/Modal';
import { groupTypeLabelKey } from '../../utils/groupHelpers';

/**
 * GroupPreviewModal - Preview the Group as visitors will see it
 * Shows the group landing page hero section in a modal overlay.
 * Using a modal (not navigation) ensures form state is preserved on close.
 */
const GroupPreviewModal = ({ isOpen, onClose, formData, imagePreview }) => {
  const { t } = useTranslation('groups');
  const groupName = formData?.name || t('untitled_group');
  const description = formData?.description || '';
  const isPrivate = formData?.is_private !== false;
  const tags = formData?.community_tags || [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      panelStyle={{
        backgroundColor: '#EDF2F8',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '700px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        margin: '20px'
      }}
    >
        {/* Preview Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px',
          backgroundColor: '#BB4D9A',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Eye size={20} />
            <span style={{ fontSize: '16px', fontWeight: '600' }}>{t('preview_modal_title')}</span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            <X size={20} color="white" />
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div style={{ flex: 1, overflow: 'auto', padding: '32px' }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Hero: Image + Title side by side */}
            <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', marginBottom: '24px' }}>
              {/* Cover Image */}
              <div style={{
                width: '160px',
                flexShrink: 0,
                aspectRatio: '1 / 1',
                borderRadius: '12px',
                overflow: 'hidden',
                backgroundColor: '#e5e7eb'
              }}>
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt={groupName}
                    loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#BB4D9A'
                  }}>
                    <ImageIcon size={40} color="white" />
                  </div>
                )}
              </div>

              {/* Name, Description, Privacy */}
              <div style={{ flex: 1, paddingTop: '8px' }}>
                <h1 style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: '#BB4D9A',
                  lineHeight: '1.1',
                  marginBottom: '12px'
                }}>
                  {groupName}
                </h1>
                {description && (
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    lineHeight: '1.6',
                    marginBottom: '16px'
                  }}>
                    {description}
                  </p>
                )}
                {/* Privacy badge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {isPrivate
                    ? <Lock size={14} color="#6b7280" />
                    : <Globe size={14} color="#6b7280" />
                  }
                  <span style={{ fontSize: '13px', color: '#6b7280' }}>
                    {formData?.group_access ? t(groupTypeLabelKey(formData)) : (isPrivate ? t('private_group') : t('public_group'))}
                  </span>
                </div>
              </div>
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div style={{ paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '4px 12px',
                        backgroundColor: '#f3e8ff',
                        color: '#7e22ce',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Close Button */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '12px 32px',
              background: '#BB4D9A',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'opacity 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            {t('close_preview')}
          </button>
        </div>
    </Modal>
  );
};

export default GroupPreviewModal;
